/**
 * Create an asset from files: start an extraction, poll its status every 3 s until it ends, read the proposal once,
 * and save the reviewed proposal as a new draft in one request.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import type { AssetOut } from '../types/assets';
import type { ExtractionIn, ExtractionStatusOut, FromExtractionIn } from '../types/extractions';
import type { ExtractionProposal } from '../types/refinements';
import { queryKeys } from './keys';

/** The extraction is one model call over the files; the screen asks again every 3 s while it runs. */
export const EXTRACTION_POLL_MS = 3000;

export const extractionKeys = {
  status: (id: string) => ['catalog', 'extraction', id, 'status'] as const,
  proposal: (id: string) => ['catalog', 'extraction', id, 'proposal'] as const,
};

const running = (status: ExtractionStatusOut | undefined) => status?.status === 'queued' || status?.status === 'running';

/** The screen shows the outcome (the status panel), so no toast. */
export function useStartExtraction() {
  return useMutation({
    meta: { quiet: true },
    mutationFn: (body: ExtractionIn) => api<ExtractionStatusOut>('/api/catalog/extractions', 'POST', body),
  });
}

export function useExtractionStatus(id: string | null) {
  return useQuery({
    queryKey: extractionKeys.status(id ?? ''),
    queryFn: () => api<ExtractionStatusOut>(`/api/catalog/extractions/${id}/status`),
    enabled: id !== null,
    refetchInterval: (query) =>
      query.state.status !== 'error' && (query.state.data === undefined || running(query.state.data)) ? EXTRACTION_POLL_MS : false,
  });
}

/**
 * Read once, when the extraction succeeded: the proposal does not change. Create from files and refine with AI share
 * the endpoint; the screen narrows on `mode`.
 */
export function useExtractionProposal(id: string | null, ready: boolean) {
  return useQuery({
    queryKey: extractionKeys.proposal(id ?? ''),
    queryFn: () => api<ExtractionProposal>(`/api/catalog/extractions/${id}`),
    enabled: id !== null && ready,
    staleTime: Infinity,
  });
}

export function useCreateFromExtraction() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (asset: AssetOut) => `${asset.name} created as a draft` },
    mutationFn: (body: FromExtractionIn) => api<AssetOut>('/api/assets/from-extraction', 'POST', body),
    onSuccess: (asset) => {
      client.setQueryData(queryKeys.asset(asset.id), asset);
      void client.invalidateQueries({ queryKey: ['assets', 'list'] });
      // The reviewed partners may include names the catalog did not have yet.
      void client.invalidateQueries({ queryKey: queryKeys.partners });
    },
  });
}
